import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { FaCheckCircle, FaDownload, FaLinkedinIn } from 'react-icons/fa';
import { Button, SectionHeader, Card, Badge, AnimatedSection, StaggerContainer, StaggerItem } from '../components/common';
import { personalInfo, summary, values, stats, certifications } from '../data/profileData';

const About = () => {
  return (
    <>
      <Helmet>
        <title>About Me | Rakesh Kumar Tripathi</title>
        <meta name="description" content={`Learn about ${personalInfo.name}'s professional journey, expertise, and values. ${summary.short}`} />
      </Helmet>

      {/* Hero Section */}
      <section className="pt-8 pb-16 gradient-bg-subtle">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Image Section */}
            <AnimatedSection animation="fadeRight">
              <div className="relative">
                <div className="absolute -top-4 -left-4 w-full h-full bg-gradient-to-br from-primary-500 to-secondary-500 rounded-2xl" />
                <div className="relative bg-gray-200 rounded-2xl aspect-square overflow-hidden">
                  <img
                    src="/images/profile-about.jpg"
                    alt="Rakesh Kumar Tripathi"
                    className="w-full h-full object-cover"
                  />
                </div>

                {/* Experience Badge */}
                <motion.div
                  className="absolute -bottom-6 -right-6 bg-white rounded-xl shadow-strong p-4"
                  animate={{ y: [0, -5, 0] }}
                  transition={{ duration: 3, repeat: Infinity }}
                >
                  <div className="text-center">
                    <div className="text-3xl font-bold text-primary-500">25+</div>
                    <div className="text-sm text-dark-500">Years Experience</div>
                  </div>
                </motion.div>
              </div>
            </AnimatedSection>

            {/* Content Section */}
            <AnimatedSection animation="fadeLeft" delay={0.2}>
              <Badge variant="primary" className="mb-4">About Me</Badge>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-dark-800 mb-6">
                {personalInfo.name}
              </h1>
              <h2 className="text-xl text-primary-500 font-medium mb-4">
                {personalInfo.title} at Turacoz Healthcare Solutions and Director Systems at FIT Group of Institutions
              </h2>
              <p className="text-dark-500 leading-relaxed mb-6">
                {summary.full.split('\n\n')[0]}
              </p>
              <p className="text-dark-500 leading-relaxed mb-8">
                {summary.full.split('\n\n')[1]}
              </p>

              <div>
                <div className="flex flex-wrap gap-4 mb-2">
                  <Button
                    variant="primary"
                    href={personalInfo.resumeLink}
                    icon={<FaDownload />}
                  >
                    Download Resume
                  </Button>
                  <Button
                    variant="outline"
                    href={personalInfo.linkedin}
                    target="_blank"
                    icon={<FaLinkedinIn />}
                  >
                    LinkedIn Profile
                  </Button>
                </div>
                <p className="text-xs text-dark-400 italic">
                  By accessing this resume, you acknowledge that the information is provided for professional review only.
                </p>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 bg-white">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                className="text-center p-6 rounded-xl bg-gradient-to-br from-primary-50 to-secondary-50"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="text-4xl font-bold text-primary-500 mb-2">{stat.value}</div>
                <div className="text-dark-500 text-sm">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Full Bio Section */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <SectionHeader
            badge="My Story"
            title="Professional Journey"
            subtitle="A detailed look at my career path and expertise"
            align="left"
          />

          <div className="grid lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <AnimatedSection>
                <Card padding="lg">
                  <div className="prose prose-lg max-w-none">
                    {summary.full.split('\n\n').map((paragraph, index) => (
                      <p key={index} className="text-dark-600 leading-relaxed mb-4">
                        {paragraph}
                      </p>
                    ))}
                  </div>
                </Card>
              </AnimatedSection>
            </div>

            <div>
              <AnimatedSection delay={0.2}>
                <Card padding="lg" className="sticky top-24">
                  <h3 className="text-lg font-bold text-dark-800 mb-4">Key Highlights</h3>
                  <ul className="space-y-3">
                    {summary.highlights.map((highlight, index) => (
                      <li key={index} className="flex items-start space-x-3">
                        <FaCheckCircle className="w-5 h-5 text-secondary-500 mt-0.5 flex-shrink-0" />
                        <span className="text-dark-600 text-sm">{highlight}</span>
                      </li>
                    ))}
                  </ul>
                </Card>
              </AnimatedSection>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <SectionHeader
            badge="My Values"
            title="What Drives Me"
            subtitle="Core principles that guide my professional journey"
          />

          <StaggerContainer className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <StaggerItem key={index}>
                <Card elevated className="text-center h-full">
                  <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-primary-100 to-secondary-100 rounded-full flex items-center justify-center">
                    <span className="text-2xl font-bold text-primary-500">
                      {value.title.charAt(0)}
                    </span>
                  </div>
                  <h3 className="text-lg font-bold text-dark-800 mb-2">{value.title}</h3>
                  <p className="text-dark-500 text-sm">{value.description}</p>
                </Card>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* Certifications Section */}
      <section className="section-padding gradient-bg-subtle">
        <div className="container-custom">
          <SectionHeader
            badge="Certifications"
            title="Professional Credentials"
            subtitle="Recognized certifications validating my expertise"
          />

          <StaggerContainer className="grid md:grid-cols-3 gap-6">
            {certifications.map((cert) => (
              <StaggerItem key={cert.id}>
                <Card elevated className="h-full">
                  <div className="flex items-start space-x-4">
                    <div className="w-16 h-16 bg-primary-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <span className="text-xl font-bold text-primary-500">
                        {cert.issuer.charAt(0)}
                      </span>
                    </div>
                    <div>
                      <h3 className="font-bold text-dark-800 mb-1">{cert.name}</h3>
                      <p className="text-primary-500 text-sm font-medium mb-2">{cert.issuer}</p>
                      <Badge variant="secondary" size="sm">{cert.year}</Badge>
                    </div>
                  </div>
                  <p className="text-dark-500 text-sm mt-4">{cert.description}</p>
                </Card>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* Industries Section */}
      <section className="section-padding bg-dark-800">
        <div className="container-custom">
          <SectionHeader
            badge="Industries"
            title="Diverse Experience"
            subtitle="Worked across multiple industries bringing technology solutions"
            titleClassName="text-white"
            subtitleClassName="text-gray-400"
          />

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {[
              'Healthcare',
              'Education',
              'Logistics',
              'Infrastructure',
              'E-commerce',
              'Manufacturing',
            ].map((industry, index) => (
              <motion.div
                key={industry}
                className="bg-dark-700 rounded-xl p-6 text-center hover:bg-dark-600 transition-colors"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -4 }}
              >
                <div className="text-white font-medium">{industry}</div>
              </motion.div>
            ))}
          </div>

          <motion.div
            className="mt-12 text-center"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            <p className="text-gray-400 max-w-2xl mx-auto">
              Including major infrastructure projects like the Bullet Train Project and Delhi Metro Projects,
              demonstrating capability to handle large-scale, mission-critical IT implementations.
            </p>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default About;
